import React, { useEffect, useState, Component } from 'react';
import Layout from '../../components/Layout';
import { useNavigate } from 'react-router-dom';
import { BuildingIcon, GraduationCapIcon, UsersIcon, BarChart3Icon, AlertCircleIcon, TrendingUpIcon, SearchIcon, FilterIcon, ChevronDownIcon, ArrowRightIcon, BrainIcon, SparklesIcon, CalendarIcon, CheckCircleIcon, XCircleIcon, AlertTriangleIcon, MapIcon, GlobeIcon, MessageSquareIcon, ClockIcon, ChevronRightIcon, ChevronUpIcon, BarChart2Icon, PieChartIcon, LineChartIcon, CheckIcon, XIcon, MoreHorizontalIcon, ExternalLinkIcon, MapPinIcon } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell, ComposedChart, Area } from 'recharts';
// Sample data for dashboard
const universityData = [{
  id: 1,
  name: 'UAE Business School',
  location: 'Dubai',
  programs: 12,
  activeApplications: 48,
  pendingReviews: 22,
  healthScore: 92,
  status: 'Active',
  lastUpdated: '2023-06-15'
}, {
  id: 2,
  name: 'Emirates Institute of Technology',
  location: 'Abu Dhabi',
  programs: 24,
  activeApplications: 103,
  pendingReviews: 56,
  healthScore: 87,
  status: 'Active',
  lastUpdated: '2023-06-12'
}, {
  id: 3,
  name: 'Sharjah American University',
  location: 'Sharjah',
  programs: 18,
  activeApplications: 75,
  pendingReviews: 31,
  healthScore: 94,
  status: 'Active',
  lastUpdated: '2023-06-10'
}, {
  id: 4,
  name: 'Dubai Medical College',
  location: 'Dubai',
  programs: 8,
  activeApplications: 62,
  pendingReviews: 15,
  healthScore: 89,
  status: 'Under Review',
  lastUpdated: '2023-06-05'
}, {
  id: 5,
  name: 'Abu Dhabi School of Management',
  location: 'Abu Dhabi',
  programs: 10,
  activeApplications: 42,
  pendingReviews: 18,
  healthScore: 81,
  status: 'Active',
  lastUpdated: '2023-06-08'
}];
const programTrends = [{
  id: 1,
  name: 'Master of Business Administration',
  university: 'UAE Business School',
  applications: 48,
  trend: '+12%',
  status: 'High Demand',
  skillGap: 'Low',
  nextIntake: 'Fall 2023',
  sector: 'Business',
  marketDemand: 85
}, {
  id: 2,
  name: 'Bachelor of Computer Science',
  university: 'Emirates Institute of Technology',
  applications: 112,
  trend: '+28%',
  status: 'High Demand',
  skillGap: 'Medium',
  nextIntake: 'Fall 2023',
  sector: 'Technology',
  marketDemand: 95
}, {
  id: 3,
  name: 'Master of Public Administration',
  university: 'UAE Business School',
  applications: 27,
  trend: '-5%',
  status: 'Declining',
  skillGap: 'High',
  nextIntake: 'Spring 2024',
  sector: 'Business',
  marketDemand: 65
}, {
  id: 4,
  name: 'PhD in Data Science',
  university: 'Emirates Institute of Technology',
  applications: 15,
  trend: '+8%',
  status: 'Stable',
  skillGap: 'Low',
  nextIntake: 'Spring 2024',
  sector: 'Technology',
  marketDemand: 88
}, {
  id: 5,
  name: 'Bachelor of Nursing',
  university: 'Dubai Medical College',
  applications: 62,
  trend: '+18%',
  status: 'High Demand',
  skillGap: 'Critical',
  nextIntake: 'Fall 2023',
  sector: 'Healthcare',
  marketDemand: 92
}];
const aiInsights = [{
  id: 1,
  type: 'alert',
  title: 'Program Review Backlog',
  message: "5 programs at Dubai Medical College have been in 'Under Review' status for over 30 days.",
  action: 'Review Programs',
  severity: 'high',
  icon: <AlertCircleIcon className="w-5 h-5 text-red-500" data-id="element-1129" />
}, {
  id: 2,
  type: 'trend',
  title: 'Skill Gap Alert',
  message: 'Critical nursing skill gap detected. Applications for nursing programs have increased by 18% while program capacity remains static.',
  action: 'View Skill Analysis',
  severity: 'medium',
  icon: <BrainIcon className="w-5 h-5 text-orange-500" data-id="element-1130" />
}, {
  id: 3,
  type: 'forecast',
  title: 'Application Forecast',
  message: 'Predicted 32% increase in technology program applications for Fall 2023 based on current trends and market demand.',
  action: 'View Forecast',
  severity: 'info',
  icon: <TrendingUpIcon className="w-5 h-5 text-blue-500" data-id="element-1131" />
}, {
  id: 4,
  type: 'mismatch',
  title: 'Program-Market Mismatch',
  message: '3 business programs at Sharjah American University show curriculum misalignment with current market needs.',
  action: 'View Details',
  severity: 'medium',
  icon: <AlertTriangleIcon className="w-5 h-5 text-orange-500" data-id="element-1132" />
}];
// Sample data for charts
const applicationVolumeData = [{
  month: 'Jan',
  applications: 156
}, {
  month: 'Feb',
  applications: 172
}, {
  month: 'Mar',
  applications: 185
}, {
  month: 'Apr',
  applications: 210
}, {
  month: 'May',
  applications: 265
}, {
  month: 'Jun',
  applications: 330
}];
const skillGapBySectorData = [{
  sector: 'Technology',
  critical: 2,
  high: 5,
  medium: 8,
  low: 12
}, {
  sector: 'Business',
  critical: 1,
  high: 7,
  medium: 10,
  low: 6
}, {
  sector: 'Healthcare',
  critical: 4,
  high: 8,
  medium: 5,
  low: 3
}, {
  sector: 'Education',
  critical: 1,
  high: 3,
  medium: 7,
  low: 9
}, {
  sector: 'Engineering',
  critical: 2,
  high: 4,
  medium: 9,
  low: 7
}];
const approvalQueueData = [{
  week: 'Week 1',
  urgent: 5,
  standard: 12
}, {
  week: 'Week 2',
  urgent: 8,
  standard: 15
}, {
  week: 'Week 3',
  urgent: 12,
  standard: 18
}, {
  week: 'Week 4',
  urgent: 7,
  standard: 14
}];
const universityByRegionData = [{
  region: 'Dubai',
  universities: 3,
  activePrograms: 28,
  applications: 186
}, {
  region: 'Abu Dhabi',
  universities: 2,
  activePrograms: 34,
  applications: 145
}, {
  region: 'Sharjah',
  universities: 1,
  activePrograms: 18,
  applications: 75
}];
const programTrendsChartData = programTrends.map(program => ({
  name: program.name.length > 20 ? program.name.substring(0, 20) + '...' : program.name,
  applications: program.applications,
  marketDemand: program.marketDemand,
  skillGap: program.skillGap === 'Critical' ? 100 : program.skillGap === 'High' ? 75 : program.skillGap === 'Medium' ? 50 : 25,
  sector: program.sector
}));
const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8'];
const SKILL_GAP_COLORS = {
  critical: '#ef4444',
  high: '#f97316',
  medium: '#facc15',
  low: '#22c55e'
};
// AI Copilot Component
const AICopilot = () => {
  const [query, setQuery] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const handleSubmit = e => {
    e.preventDefault();
    if (query.trim()) {
      setIsTyping(true);
      setTimeout(() => {
        setIsTyping(false);
        setShowResults(true);
      }, 1500);
    }
  };
  return;
};
// University Status Badge Component
const StatusBadge = ({
  status
}) => {
  const getStatusColor = () => {
    switch (status) {
      case 'Active':
        return 'bg-green-100 text-green-800';
      case 'Under Review':
        return 'bg-yellow-100 text-yellow-800';
      case 'Inactive':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  return <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor()}`} data-id="element-1133">
      {status}
    </span>;
};
// Health Score Component
const HealthScore = ({
  score
}) => {
  const getBarColor = () => {
    if (score >= 90) return 'bg-green-500';
    if (score >= 80) return 'bg-blue-500';
    if (score >= 70) return 'bg-yellow-500';
    return 'bg-red-500';
  };
  const getTextColor = () => {
    if (score >= 90) return 'text-green-600';
    if (score >= 80) return 'text-blue-600';
    if (score >= 70) return 'text-yellow-600';
    return 'text-red-600';
  };
  const barColorClass = getBarColor();
  const textColorClass = getTextColor();
  const barWidth = `${score}%`;
  return <div className="flex items-center" data-id="element-1134">
      <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden mr-2" data-id="element-1135">
        <div className={`h-full ${barColorClass}`} style={{
        width: barWidth
      }} data-id="element-1136"></div>
      </div>
      <span className={`text-sm font-medium ${textColorClass}`} data-id="element-1137">{score}</span>
    </div>;
};
// Trend Indicator Component
const TrendIndicator = ({
  trend
}) => {
  const isPositive = trend.startsWith('+');
  return <span className={`inline-flex items-center ${isPositive ? 'text-green-600' : 'text-red-600'}`} data-id="element-1138">
      {isPositive ? <TrendingUpIcon className="w-4 h-4 mr-1" data-id="element-1139" /> : <TrendingUpIcon className="w-4 h-4 mr-1 transform rotate-180" data-id="element-1140" />}
      {trend}
    </span>;
};
// Skill Gap Indicator Component
const SkillGapIndicator = ({
  gap
}) => {
  const getGapColor = () => {
    switch (gap) {
      case 'Critical':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'High':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'Medium':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'Low':
        return 'bg-green-100 text-green-800 border-green-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };
  return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getGapColor()}`} data-id="element-1141">
      {gap}
    </span>;
};
// Card component with collapsible content
const CollapsibleCard = ({
  title,
  icon,
  children,
  initiallyExpanded = true,
  stats,
  action
}) => {
  const [isExpanded, setIsExpanded] = useState(initiallyExpanded);
  return <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden" data-id="element-1142">
      <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 flex justify-between items-center" data-id="element-1143">
        <div className="flex items-center" data-id="element-1144">
          {icon}
          <h2 className="text-lg font-medium text-gray-800 ml-2" data-id="element-1145">{title}</h2>
        </div>
        <div className="flex items-center space-x-2" data-id="element-1146">
          {action && <button onClick={action.onClick} className="text-sm text-primary-500 hover:underline flex items-center" data-id="element-1147">
              {action.label}
              <ArrowRightIcon className="w-4 h-4 ml-1" data-id="element-1148" />
            </button>}
          <button onClick={() => setIsExpanded(!isExpanded)} className="text-gray-500 hover:text-gray-700" data-id="element-1149">
            {isExpanded ? <ChevronUpIcon className="w-5 h-5" data-id="element-1150" /> : <ChevronDownIcon className="w-5 h-5" data-id="element-1151" />}
          </button>
        </div>
      </div>
      {!isExpanded && stats && <div className="p-4 flex flex-wrap gap-4" data-id="element-1152">
          {stats.map((stat, index) => <div key={index} className="flex items-center" data-id="element-1153">
              {stat.icon && <span className="mr-2" data-id="element-1154">{stat.icon}</span>}
              <div data-id="element-1155">
                <div className="text-xl font-semibold" data-id="element-1156">{stat.value}</div>
                <div className="text-xs text-gray-500" data-id="element-1157">{stat.label}</div>
              </div>
            </div>)}
        </div>}
      {isExpanded && <div className="p-4" data-id="element-1158">{children}</div>}
    </div>;
};
// Alert Item Component
const AlertItem = ({
  alert,
  onDismiss
}) => {
  return <div className={`p-4 rounded-lg border mb-3 ${alert.severity === 'high' ? 'bg-red-50 border-red-100' : alert.severity === 'medium' ? 'bg-orange-50 border-orange-100' : 'bg-blue-50 border-blue-100'}`} data-id="element-1159">
      <div className="flex items-start" data-id="element-1160">
        {alert.icon}
        <div className="ml-3 flex-grow" data-id="element-1161">
          <div className="flex justify-between items-start" data-id="element-1162">
            <h3 className={`font-medium ${alert.severity === 'high' ? 'text-red-800' : alert.severity === 'medium' ? 'text-orange-800' : 'text-blue-800'} mb-1`} data-id="element-1163">
              {alert.title}
            </h3>
            {onDismiss && <button onClick={() => onDismiss(alert.id)} className="text-gray-400 hover:text-gray-600" data-id="element-1164">
                <XIcon className="w-4 h-4" data-id="element-1165" />
              </button>}
          </div>
          <p className={`text-sm ${alert.severity === 'high' ? 'text-red-700' : alert.severity === 'medium' ? 'text-orange-700' : 'text-blue-700'} mb-3`} data-id="element-1166">
            {alert.message}
          </p>
          <button className={`px-3 py-1.5 text-xs rounded-md ${alert.severity === 'high' ? 'bg-red-600 text-white' : alert.severity === 'medium' ? 'bg-orange-600 text-white' : 'bg-blue-600 text-white'} hover:bg-opacity-90 transition-colors flex items-center`} data-id="element-1167">
            {alert.action}
            <ArrowRightIcon className="w-3 h-3 ml-1" data-id="element-1168" />
          </button>
        </div>
      </div>
    </div>;
};
// Custom tooltip for charts
const CustomTooltip = ({
  active,
  payload,
  label
}) => {
  if (active && payload && payload.length) {
    return <div className="bg-white p-3 border border-gray-200 shadow-md rounded-md" data-id="element-1169">
        <p className="font-medium text-gray-700" data-id="element-1170">{label}</p>
        {payload.map((entry, index) => <p key={index} style={{
        color: entry.color
      }} className="text-sm" data-id="element-1171">
            {entry.name}: {entry.value}
          </p>)}
      </div>;
  }
  return null;
};
const AdminDashboard = () => {
  const navigate = useNavigate();
  const [universityFilter, setUniversityFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeInsights, setActiveInsights] = useState(aiInsights);
  const [selectedRegion, setSelectedRegion] = useState('all');
  // Filter universities based on search query
  const filteredUniversities = universityData.filter(university => university.name.toLowerCase().includes(searchQuery.toLowerCase()) || university.location.toLowerCase().includes(searchQuery.toLowerCase()));
  // Dismiss an insight
  const dismissInsight = id => {
    setActiveInsights(activeInsights.filter(insight => insight.id !== id));
  };
  // Filter university data by region for chart
  const filteredUniversityByRegion = selectedRegion === 'all' ? universityByRegionData : universityByRegionData.filter(data => data.region === selectedRegion);
  return <Layout title="Platform Administration" persona="Platform Admin" data-id="element-1172">
      <div className="space-y-4" data-id="element-1173">
        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-1174">
          <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow h-full" data-id="element-1175">
            <div className="flex items-start justify-between" data-id="element-1176">
              <div data-id="element-1177">
                <h3 className="font-medium text-gray-800 mb-1" data-id="element-1178">Universities</h3>
                <div className="text-3xl font-bold text-primary-500" data-id="element-1179">5</div>
                <p className="text-sm text-gray-500 mt-1" data-id="element-1180">1 under review</p>
              </div>
              <div className="p-3 bg-primary-50 rounded-full" data-id="element-1181">
                <BuildingIcon className="w-6 h-6 text-primary-500" data-id="element-1182" />
              </div>
            </div>
            <div className="mt-3 pt-3 border-t border-gray-100" data-id="element-1183">
              <button onClick={() => navigate('/admin/universities')} className="text-sm text-primary-500 font-medium flex items-center hover:underline" data-id="element-1184">
                Manage universities
                <ArrowRightIcon className="w-4 h-4 ml-1" data-id="element-1185" />
              </button>
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow h-full" data-id="element-1186">
            <div className="flex items-start justify-between" data-id="element-1187">
              <div data-id="element-1188">
                <h3 className="font-medium text-gray-800 mb-1" data-id="element-1189">
                  Active Programs
                </h3>
                <div className="text-3xl font-bold text-primary-500" data-id="element-1190">72</div>
                <p className="text-sm text-gray-500 mt-1" data-id="element-1191">8 pending approval</p>
              </div>
              <div className="p-3 bg-primary-50 rounded-full" data-id="element-1192">
                <GraduationCapIcon className="w-6 h-6 text-primary-500" data-id="element-1193" />
              </div>
            </div>
            <div className="mt-3 pt-3 border-t border-gray-100" data-id="element-1194">
              <button onClick={() => navigate('/admin/programs')} className="text-sm text-primary-500 font-medium flex items-center hover:underline" data-id="element-1195">
                View all programs
                <ArrowRightIcon className="w-4 h-4 ml-1" data-id="element-1196" />
              </button>
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow h-full" data-id="element-1197">
            <div className="flex items-start justify-between" data-id="element-1198">
              <div data-id="element-1199">
                <h3 className="font-medium text-gray-800 mb-1" data-id="element-1200">Applications</h3>
                <div className="text-3xl font-bold text-primary-500" data-id="element-1201">330</div>
                <p className="text-sm text-gray-500 mt-1" data-id="element-1202">142 pending review</p>
              </div>
              <div className="p-3 bg-primary-50 rounded-full" data-id="element-1203">
                <UsersIcon className="w-6 h-6 text-primary-500" data-id="element-1204" />
              </div>
            </div>
            <div className="mt-3 pt-3 border-t border-gray-100" data-id="element-1205">
              <button onClick={() => navigate('/admin/applications')} className="text-sm text-primary-500 font-medium flex items-center hover:underline" data-id="element-1206">
                Monitor applications
                <ArrowRightIcon className="w-4 h-4 ml-1" data-id="element-1207" />
              </button>
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow h-full" data-id="element-1208">
            <div className="flex items-start justify-between" data-id="element-1209">
              <div data-id="element-1210">
                <h3 className="font-medium text-gray-800 mb-1" data-id="element-1211">
                  Average Health Score
                </h3>
                <div className="text-3xl font-bold text-primary-500" data-id="element-1212">88.6</div>
                <p className="text-sm text-gray-500 mt-1" data-id="element-1213">
                  +2.3 from last quarter
                </p>
              </div>
              <div className="p-3 bg-primary-50 rounded-full" data-id="element-1214">
                <BarChart3Icon className="w-6 h-6 text-primary-500" data-id="element-1215" />
              </div>
            </div>
            <div className="mt-3 pt-3 border-t border-gray-100" data-id="element-1216">
              <button onClick={() => navigate('/admin/analytics')} className="text-sm text-primary-500 font-medium flex items-center hover:underline" data-id="element-1217">
                View analytics
                <ArrowRightIcon className="w-4 h-4 ml-1" data-id="element-1218" />
              </button>
            </div>
          </div>
        </div>
        {/* Main Dashboard Content - Two rows of charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-id="element-1219">
          {/* Application Volume Chart */}
          <div className="lg:col-span-2" data-id="element-1220">
            <CollapsibleCard title="Application Volume Over Time" icon={<LineChartIcon className="w-5 h-5 text-primary-500" data-id="element-1222" />} action={{
            label: 'View Details',
            onClick: () => navigate('/admin/analytics')
          }} data-id="element-1221">
              <div className="h-64" data-id="element-1223">
                <ResponsiveContainer width="100%" height="100%" data-id="element-1224">
                  <LineChart data={applicationVolumeData} margin={{
                  top: 5,
                  right: 30,
                  left: 20,
                  bottom: 5
                }} data-id="element-1225">
                    <CartesianGrid strokeDasharray="3 3" data-id="element-1226" />
                    <XAxis dataKey="month" data-id="element-1227" />
                    <YAxis data-id="element-1228" />
                    <Tooltip content={<CustomTooltip data-id="element-1230" />} data-id="element-1229" />
                    <Legend data-id="element-1231" />
                    <Line type="monotone" dataKey="applications" stroke="#4f46e5" strokeWidth={2} activeDot={{
                    r: 8
                  }} data-id="element-1232" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CollapsibleCard>
          </div>
          {/* Universities by Region */}
          <div className="flex flex-col h-full" data-id="element-1233">
            <CollapsibleCard title="Universities by Region" icon={<MapPinIcon className="w-5 h-5 text-primary-500" data-id="element-1235" />} action={{
            label: 'View All',
            onClick: () => navigate('/admin/universities')
          }} stats={[{
            value: '5',
            label: 'Total Universities',
            icon: <BuildingIcon className="w-4 h-4 text-primary-500" data-id="element-1236" />
          }, {
            value: '72',
            label: 'Active Programs',
            icon: <GraduationCapIcon className="w-4 h-4 text-blue-500" data-id="element-1237" />
          }, {
            value: '330',
            label: 'Applications',
            icon: <UsersIcon className="w-4 h-4 text-green-500" data-id="element-1238" />
          }]} className="h-full flex-grow" data-id="element-1234">
              <div className="mb-3 flex flex-wrap gap-2" data-id="element-1239">
                <button onClick={() => setSelectedRegion('all')} className={`px-3 py-1 text-sm rounded-full ${selectedRegion === 'all' ? 'bg-primary-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-1240">
                  All Regions
                </button>
                {universityByRegionData.map((region, index) => <button key={index} onClick={() => setSelectedRegion(region.region)} className={`px-3 py-1 text-sm rounded-full ${selectedRegion === region.region ? 'bg-primary-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-1241">
                    {region.region}
                  </button>)}
              </div>
              <div className="h-60" data-id="element-1242">
                <ResponsiveContainer width="100%" height="100%" data-id="element-1243">
                  <BarChart data={filteredUniversityByRegion} margin={{
                  top: 5,
                  right: 30,
                  left: 20,
                  bottom: 5
                }} data-id="element-1244">
                    <CartesianGrid strokeDasharray="3 3" data-id="element-1245" />
                    <XAxis dataKey="region" data-id="element-1246" />
                    <YAxis data-id="element-1247" />
                    <Tooltip content={<CustomTooltip data-id="element-1249" />} data-id="element-1248" />
                    <Legend data-id="element-1250" />
                    <Bar dataKey="universities" name="Universities" fill="#4f46e5" data-id="element-1251" />
                    <Bar dataKey="activePrograms" name="Programs" fill="#3b82f6" data-id="element-1252" />
                    <Bar dataKey="applications" name="Applications" fill="#10b981" data-id="element-1253" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="mt-3 overflow-x-auto" data-id="element-1254">
                <table className="min-w-full divide-y divide-gray-200" data-id="element-1255">
                  <thead className="bg-gray-50" data-id="element-1256">
                    <tr data-id="element-1257">
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1258">
                        University
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1259">
                        Location
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1260">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200" data-id="element-1261">
                    {filteredUniversities.slice(0, 3).map(university => <tr key={university.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/admin/universities/${university.id}`)} data-id="element-1262">
                        <td className="px-3 py-2 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-1263">
                          {university.name}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-1264">
                          {university.location}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap" data-id="element-1265">
                          <StatusBadge status={university.status} data-id="element-1266" />
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>
            </CollapsibleCard>
          </div>
          {/* AI Insights & Alerts */}
          <div className="flex flex-col h-full" data-id="element-1267">
            <CollapsibleCard title="AI Insights & Alerts" icon={<SparklesIcon className="w-5 h-5 text-primary-500" data-id="element-1269" />} stats={[{
            value: activeInsights.filter(i => i.severity === 'high').length,
            label: 'High Priority',
            icon: <AlertCircleIcon className="w-4 h-4 text-red-500" data-id="element-1270" />
          }, {
            value: activeInsights.filter(i => i.severity === 'medium').length,
            label: 'Medium Priority',
            icon: <AlertTriangleIcon className="w-4 h-4 text-orange-500" data-id="element-1271" />
          }]} className="h-full flex-grow" data-id="element-1268">
              <div className="space-y-2 overflow-y-auto" style={{
              maxHeight: '450px',
              height: '100%'
            }} data-id="element-1272">
                {activeInsights.map(insight => <AlertItem key={insight.id} alert={insight} onDismiss={dismissInsight} data-id="element-1273" />)}
                {activeInsights.length === 0 && <div className="text-center py-6" data-id="element-1274">
                    <CheckCircleIcon className="mx-auto h-10 w-10 text-green-500" data-id="element-1275" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900" data-id="element-1276">
                      All caught up!
                    </h3>
                    <p className="mt-1 text-sm text-gray-500" data-id="element-1277">
                      No active alerts or insights
                    </p>
                  </div>}
              </div>
            </CollapsibleCard>
          </div>
        </div>
        {/* Three charts in one row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-id="element-1278">
          {/* Program Trends Chart */}
          <div className="flex flex-col h-full" data-id="element-1279">
            <CollapsibleCard title="Program Trends" icon={<BarChart2Icon className="w-5 h-5 text-primary-500" data-id="element-1281" />} action={{
            label: 'View All',
            onClick: () => navigate('/admin/programs')
          }} className="h-full flex-grow" data-id="element-1280">
              <div className="h-60" data-id="element-1282">
                <ResponsiveContainer width="100%" height="100%" data-id="element-1283">
                  <ComposedChart data={programTrendsChartData} margin={{
                  top: 5,
                  right: 10,
                  left: 10,
                  bottom: 5
                }} data-id="element-1284">
                    <CartesianGrid strokeDasharray="3 3" data-id="element-1285" />
                    <XAxis dataKey="name" data-id="element-1286" />
                    <YAxis data-id="element-1287" />
                    <Tooltip content={<CustomTooltip data-id="element-1289" />} data-id="element-1288" />
                    <Legend data-id="element-1290" />
                    <Bar dataKey="applications" name="Applications" fill="#4f46e5" data-id="element-1291" />
                    <Line type="monotone" dataKey="marketDemand" name="Market Demand" stroke="#10b981" strokeWidth={2} data-id="element-1292" />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </CollapsibleCard>
          </div>
          {/* Skill Gap by Sector */}
          <div className="flex flex-col h-full" data-id="element-1293">
            <CollapsibleCard title="Skill Gap Severity" icon={<PieChartIcon className="w-5 h-5 text-primary-500" data-id="element-1295" />} action={{
            label: 'Analysis',
            onClick: () => navigate('/admin/analytics')
          }} className="h-full flex-grow" data-id="element-1294">
              <div className="h-60" data-id="element-1296">
                <ResponsiveContainer width="100%" height="100%" data-id="element-1297">
                  <BarChart data={skillGapBySectorData.slice(0, 3)} margin={{
                  top: 5,
                  right: 10,
                  left: 10,
                  bottom: 5
                }} layout="vertical" data-id="element-1298">
                    <CartesianGrid strokeDasharray="3 3" data-id="element-1299" />
                    <XAxis type="number" data-id="element-1300" />
                    <YAxis dataKey="sector" type="category" width={80} data-id="element-1301" />
                    <Tooltip content={<CustomTooltip data-id="element-1303" />} data-id="element-1302" />
                    <Legend data-id="element-1304" />
                    <Bar dataKey="critical" name="Critical" stackId="a" fill={SKILL_GAP_COLORS.critical} data-id="element-1305" />
                    <Bar dataKey="high" name="High" stackId="a" fill={SKILL_GAP_COLORS.high} data-id="element-1306" />
                    <Bar dataKey="medium" name="Medium" stackId="a" fill={SKILL_GAP_COLORS.medium} data-id="element-1307" />
                    <Bar dataKey="low" name="Low" stackId="a" fill={SKILL_GAP_COLORS.low} data-id="element-1308" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CollapsibleCard>
          </div>
          {/* Approval Queue Forecast */}
          <div className="flex flex-col h-full" data-id="element-1309">
            <CollapsibleCard title="Approval Queue" icon={<ClockIcon className="w-5 h-5 text-primary-500" data-id="element-1311" />} action={{
            label: 'Manage',
            onClick: () => navigate('/admin/applications')
          }} className="h-full flex-grow" data-id="element-1310">
              <div className="h-60" data-id="element-1312">
                <ResponsiveContainer width="100%" height="100%" data-id="element-1313">
                  <BarChart data={approvalQueueData} margin={{
                  top: 5,
                  right: 10,
                  left: 10,
                  bottom: 5
                }} data-id="element-1314">
                    <CartesianGrid strokeDasharray="3 3" data-id="element-1315" />
                    <XAxis dataKey="week" data-id="element-1316" />
                    <YAxis data-id="element-1317" />
                    <Tooltip content={<CustomTooltip data-id="element-1319" />} data-id="element-1318" />
                    <Legend data-id="element-1320" />
                    <Bar dataKey="urgent" name="Urgent" fill="#ef4444" data-id="element-1321" />
                    <Bar dataKey="standard" name="Standard" fill="#3b82f6" data-id="element-1322" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              
            </CollapsibleCard>
          </div>
        </div>
      </div>
    </Layout>;
};
export default AdminDashboard;